import { AbstractModel, DateUtil, isNullOrUndefined } from '@uzurv/core';
import { autoserialize, autoserializeAs, inheritSerialization } from 'cerialize';
import { CancellationReason } from './cancellation-reason';
import { ChatMessage } from './chat-message';
import { ClientFeeScheduleDetail } from './client-fee-schedule-detail';
import { Driver } from './driver';
import { DriverRideConfiguration } from './driver-ride-configuration';
import { OrganizationPaymentMethod } from './organization-payment-method';
import { Provider } from './provider';
import { ProviderFeeScheduleDetail } from './provider-fee-schedule-detail';
import { ReservationStatus } from './reservation-status';
import { RideRequestDetail } from './ride-request-detail';
import { ServiceLevel } from './service-level';
import { Vehicle } from './vehicle';

@inheritSerialization(AbstractModel)
export class ReservationDetail extends AbstractModel {
  @autoserializeAs(Driver)
  driver: Driver;

  @autoserializeAs(Provider)
  provider: Provider;

  @autoserializeAs(Vehicle)
  vehicle: Vehicle;

  @autoserializeAs(RideRequestDetail)
  rideRequestDetail: RideRequestDetail;

  @autoserialize
  vehicleId: number;

  @autoserialize
  riderConfirmedPickup: boolean;

  @autoserializeAs(ReservationStatus)
  reservationStatus: ReservationStatus;

  @autoserializeAs(Date)
  completedDate: Date;

  @autoserialize
  cancelled: boolean;

  @autoserializeAs(CancellationReason)
  cancellationReason: CancellationReason;

  @autoserializeAs(Date)
  cancelledDate: Date;

  @autoserialize
  riderCharged: boolean;

  @autoserialize
  driverCharged: boolean;

  @autoserializeAs(Date)
  driverEnRouteDate: Date;

  @autoserializeAs(Date)
  pickUpArrivedDate: Date;

  @autoserializeAs(Date)
  inProgressDate: Date;

  @autoserializeAs(Date)
  dropOffArrivedDate: Date;

  @autoserialize
  driverPaid: boolean;

  @autoserialize
  driverPaymentId: number;

  @autoserialize
  driverBankTransactionId: number;

  @autoserialize
  referralPaid: boolean;

  @autoserialize
  createdBy: string;

  @autoserialize
  cancelledBy: string;

  @autoserializeAs(ChatMessage)
  chatMessages: Array<ChatMessage>;

  @autoserializeAs(OrganizationPaymentMethod)
  supportedPaymentMethods: Array<OrganizationPaymentMethod>;

  @autoserializeAs(ClientFeeScheduleDetail)
  clientFeeScheduleDetail: ClientFeeScheduleDetail;

  @autoserializeAs(ProviderFeeScheduleDetail)
  providerFeeScheduleDetail: ProviderFeeScheduleDetail;

  @autoserializeAs(ServiceLevel)
  serviceLevel: ServiceLevel;

  @autoserializeAs(DriverRideConfiguration)
  driverRideConfiguration: DriverRideConfiguration;

  @autoserialize
  telematicsDrivePortalLink: string;

  get rideStartDate(): Date {
    if (!isNullOrUndefined(this.rideRequestDetail.pickupDate)) {
      return this.rideRequestDetail.pickupDate;
    } else if (!isNullOrUndefined(this.pickUpArrivedDate)) {
      return this.pickUpArrivedDate;
    } else if (!isNullOrUndefined(this.rideRequestDetail.activatedAt)) {
      return this.rideRequestDetail.activatedAt;
    } else if (this.cancelled) {
      return this.cancelledDate;
    }
    return this.modifiedDate;
  }

  static OnSerialized(reservationDetail: ReservationDetail, json: any): void {
    super.OnSerialized(reservationDetail, json);

    if (!isNullOrUndefined(reservationDetail.completedDate)) {
      json.completedDate = DateUtil.serializeDate(reservationDetail.completedDate);
    }

    if (!isNullOrUndefined(reservationDetail.cancelledDate)) {
      json.cancelledDate = DateUtil.serializeDate(reservationDetail.cancelledDate);
    }
    if (!isNullOrUndefined(reservationDetail.driverEnRouteDate)) {
      json.driverEnRouteDate = DateUtil.serializeDate(reservationDetail.driverEnRouteDate);
    }

    if (!isNullOrUndefined(reservationDetail.pickUpArrivedDate)) {
      json.pickUpArrivedDate = DateUtil.serializeDate(reservationDetail.pickUpArrivedDate);
    }

    if (!isNullOrUndefined(reservationDetail.dropOffArrivedDate)) {
      json.dropOffArrivedDate = DateUtil.serializeDate(reservationDetail.dropOffArrivedDate);
    }

    if (!isNullOrUndefined(reservationDetail.inProgressDate)) {
      json.inProgressDate = DateUtil.serializeDate(reservationDetail.inProgressDate);
    }
  }

  static OnDeserialized(instance: ReservationDetail, json: any): void {
    super.OnDeserialized(instance, json);

    if (!isNullOrUndefined(json['completedDate'])) {
      instance.completedDate = DateUtil.deserializeDate(json['completedDate']);
    }

    if (!isNullOrUndefined(json['cancelledDate'])) {
      instance.cancelledDate = DateUtil.deserializeDate(json['cancelledDate']);
    }

    if (!isNullOrUndefined(json['driverEnRouteDate'])) {
      instance.driverEnRouteDate = DateUtil.deserializeDate(json['driverEnRouteDate']);
    }

    if (!isNullOrUndefined(json['pickUpArrivedDate'])) {
      instance.pickUpArrivedDate = DateUtil.deserializeDate(json['pickUpArrivedDate']);
    }

    if (!isNullOrUndefined(json['dropOffArrivedDate'])) {
      instance.dropOffArrivedDate = DateUtil.deserializeDate(json['dropOffArrivedDate']);
    }

    if (!isNullOrUndefined(json['inProgressDate'])) {
      instance.inProgressDate = DateUtil.deserializeDate(json['inProgressDate']);
    }
  }
}
